class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpsellStrip extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    if (!productId) return;
    this._fetch(productId);
  }

  _fetch(productId) {
    fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=complementary`)
      .then((r) => r.json())
      .then((data) => {
        if (data.products && data.products.length > 0) {
          this._render(data.products);
        } else {
          return fetch(`/recommendations/products.json?product_id=${productId}&limit=4&intent=related`)
            .then((r) => r.json())
            .then((fallback) => {
              if (fallback.products && fallback.products.length > 0) {
                this._render(fallback.products);
              }
            });
        }
      })
      .catch(() => {});
  }

  _render(products) {
    const cards = products
      .map((p) => {
        const variant = p.variants.find((v) => v.available) || p.variants[0];
        if (!variant) return '';

        const imgSrc = p.featured_image
          ? p.featured_image.replace(/(\.\w+)(\?|$)/, '_160x160$1$2')
          : '';
        const imgTag = imgSrc
          ? `<img class="cro-upsell__img" src="${imgSrc}" alt="${this._esc(p.title)}" width="80" height="80" loading="lazy">`
          : `<div class="cro-upsell__img cro-upsell__img--placeholder"></div>`;

        const price = this._formatMoney(variant.price);

        return `
          <div class="cro-upsell__card" role="listitem">
            <a href="${p.url}" class="cro-upsell__img-link" tabindex="-1" aria-hidden="true">${imgTag}</a>
            <p class="cro-upsell__title">${this._esc(p.title)}</p>
            <p class="cro-upsell__price">${price}</p>
            <button
              class="cro-upsell__btn button button--secondary"
              type="button"
              data-variant-id="${variant.id}"
              aria-label="Agregar ${this._esc(p.title)} al carrito"
            >Agregar</button>
          </div>`;
      })
      .join('');

    this.innerHTML = `
      <div class="cro-upsell__header">
        <p class="cro-upsell__heading">Completa tu experiencia</p>
      </div>
      <div class="cro-upsell__track" role="list" aria-label="Productos complementarios">
        ${cards}
      </div>`;

    this.querySelectorAll('.cro-upsell__btn').forEach((btn) => {
      btn.addEventListener('click', () => this._addToCart(btn));
    });
  }

  _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    btn.textContent = '...';

    const body = JSON.stringify({
      id: parseInt(variantId),
      quantity: 1,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_add_url, {
      ...fetchConfig('javascript'),
      body,
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          btn.textContent = 'Agregar';
          btn.disabled = false;
          return;
        }
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.renderContents(response);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', productVariantId: variantId });
        }
      })
      .catch(() => {
        btn.textContent = 'Agregar';
        btn.disabled = false;
      });
  }

  _formatMoney(cents) {
    return '$' + (cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }
}

customElements.define('cart-upsell-strip', CartUpsellStrip);
